import { useEffect } from 'react';
import { THEME_COLORS } from '../constants/themeColors';
import { detectDataType, getColumnAlignment, getHeaderAlignment } from '../utils/tableUtils';

/**
 * Hook personnalisé pour appliquer automatiquement les règles d'alignement
 * à tous les tableaux dans un composant
 * 
 * RÈGLES APPLIQUÉES:
 * - Headers: CENTRÉS
 * - Texte: GAUCHE
 * - Pourcentages: CENTRÉS
 * - Chiffres/Montants: DROITE
 * - Dates: GAUCHE
 * - Couleurs du thème
 * - Largeurs automatiques
 */

export const useTableAlignment = (containerRef, data = [], options = {}) => {
  const {
    autoAlign = true,
    autoColor = true,
    autoWidth = true,
    applyHover = true
  } = options;

  useEffect(() => {
    if (!containerRef.current || !autoAlign) return;

    const container = containerRef.current;
    const tables = container.querySelectorAll('table');

    tables.forEach(table => {
      // Application des styles globaux au tableau
      if (autoColor) {
        table.style.borderCollapse = 'collapse';
        table.style.width = '100%';
        table.style.fontFamily = "'IBM Plex Sans', sans-serif";
        table.style.fontSize = '0.875rem';
        table.style.backgroundColor = THEME_COLORS.WHITE;
        table.style.border = `2px solid ${THEME_COLORS.PRIMARY}`;
        table.style.borderRadius = '6px';
        table.style.overflow = 'hidden';
        table.style.boxShadow = `0 4px 6px ${THEME_COLORS.PRIMARY}20`;
      }

      // Traitement des headers
      const headers = table.querySelectorAll('th');
      headers.forEach((header, colIndex) => {
        if (autoAlign) {
          header.style.textAlign = getHeaderAlignment();
        }
        
        if (autoColor) {
          header.style.backgroundColor = THEME_COLORS.PRIMARY;
          header.style.color = THEME_COLORS.WHITE;
          header.style.backgroundImage = `linear-gradient(135deg, ${THEME_COLORS.PRIMARY} 0%, ${THEME_COLORS.PRIMARY_DARK} 100%)`;
          header.style.fontWeight = '600';
          header.style.padding = '12px 8px';
          header.style.border = `1px solid ${THEME_COLORS.PRIMARY_DARK}`;
          header.style.textTransform = 'uppercase';
          header.style.fontSize = '0.75rem';
          header.style.letterSpacing = '0.5px';
        }
      });

      // Traitement des cellules de données
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('td');
        
        cells.forEach((cell, colIndex) => {
          const header = headers[colIndex];
          if (!header) return;
          
          const columnName = header.textContent || header.innerText || '';
          const columnKey = columnName.toLowerCase().replace(/\s+/g, '');
          const cellValue = cell.textContent || cell.innerText || '';
          
          // Détection du type et application de l'alignement
          const dataType = detectDataType(columnKey, [{ [columnKey]: cellValue }]);
          
          if (autoAlign) {
            const alignment = getColumnAlignment(columnKey, [{ [columnKey]: cellValue }]);
            cell.style.textAlign = alignment;
          }
          
          if (autoColor) {
            cell.style.color = THEME_COLORS.PRIMARY;
            cell.style.padding = '8px';
            cell.style.border = `1px solid ${THEME_COLORS.BORDER || '#E0E3E7'}`;
            cell.style.verticalAlign = 'middle';
          }
          
          // Classes et styles spécialisés selon le type
          cell.classList.remove('percentage-cell', 'number-cell', 'date-cell', 'text-cell');
          cell.classList.add(`${dataType}-cell`);
          cell.setAttribute('data-type', dataType);
          
          // Styles spéciaux pour les pourcentages
          if (dataType === 'percentage' && autoColor) {
            cell.style.backgroundColor = `${THEME_COLORS.PRIMARY}15`;
            cell.style.fontWeight = 'bold';
            cell.style.borderRadius = '4px';
          }
          
          // Police monospace pour les nombres et dates
          if ((dataType === 'number' || dataType === 'date') && autoColor) {
            cell.style.fontFamily = "'JetBrains Mono', 'Monaco', 'Courier New', monospace";
          }
          
          if (dataType === 'number' && autoColor) {
            cell.style.fontWeight = '500';
          }
        });
        
        // Effets de survol
        if (applyHover && autoColor) {
          row.addEventListener('mouseenter', () => {
            row.style.backgroundColor = `${THEME_COLORS.PRIMARY}10`;
            row.style.transition = 'background-color 0.2s ease';
            
            const cells = row.querySelectorAll('td');
            cells.forEach(cell => {
              cell.style.borderColor = THEME_COLORS.PRIMARY;
            });
          });
          
          row.addEventListener('mouseleave', () => {
            row.style.backgroundColor = 'transparent';
            
            const cells = row.querySelectorAll('td');
            cells.forEach(cell => {
              cell.style.borderColor = THEME_COLORS.BORDER || '#E0E3E7';
            });
          });
        }
      });
    });
  }, [data, autoAlign, autoColor, autoWidth, applyHover]);
};

/**
 * Hook simplifié pour l'application rapide sur un seul tableau
 */
export const useQuickTableAlign = (tableRef, data = []) => {
  return useTableAlignment(tableRef, data, {
    autoAlign: true,
    autoColor: true,
    autoWidth: true,
    applyHover: true
  });
};

/**
 * Fonction utilitaire pour appliquer manuellement les règles à un élément table
 */
export const applyTableRules = (tableElement, data = []) => {
  if (!tableElement) return;
  
  // Application des styles de base
  tableElement.style.borderCollapse = 'collapse';
  tableElement.style.width = '100%';
  tableElement.style.fontFamily = "'IBM Plex Sans', sans-serif";
  tableElement.style.fontSize = '0.875rem';
  tableElement.style.backgroundColor = THEME_COLORS.WHITE;
  
  // Headers
  const headers = tableElement.querySelectorAll('th');
  headers.forEach(header => {
    header.style.textAlign = 'center';
    header.style.backgroundColor = THEME_COLORS.PRIMARY;
    header.style.color = THEME_COLORS.WHITE;
    header.style.backgroundImage = `linear-gradient(135deg, ${THEME_COLORS.PRIMARY} 0%, ${THEME_COLORS.PRIMARY_DARK} 100%)`;
    header.style.fontWeight = '600';
    header.style.padding = '12px 8px';
    header.style.textTransform = 'uppercase';
    header.style.fontSize = '0.75rem';
  });
  
  // Cellules
  const cells = tableElement.querySelectorAll('td');
  cells.forEach(cell => {
    cell.style.color = THEME_COLORS.PRIMARY;
    cell.style.padding = '8px';
    cell.style.border = `1px solid #E0E3E7`;
  });
};

export default useTableAlignment;
